import { type Location, LocationBody } from "@cleanbudget/contract/stock";
import { Badge } from "@cleanbudget/ui/components/badge";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent } from "@cleanbudget/ui/components/card";
import { IconArchive, IconCheck, IconPencil, IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { orpc } from "@/lib/orpc";

const locationsQuery = () => orpc.stock.listLocations.queryOptions();

/**
 * Locaties (#61): where the stock lies. Add one, rename one in its row, and deactivate one that
 * holds no stock (behind a confirmation; its history stays). The default location always stays.
 * One location for now, but every screen is ready for more.
 */
export const Route = createFileRoute("/_admin/settings/location/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(locationsQuery()),
	component: LocationsPage,
});

/** A refused location write in Dutch, by its tag (`StockContract`). */
function useLocationErrorMessage() {
	const { t } = useTranslation("location");
	const { t: tCommon } = useTranslation();
	return (error: unknown): string =>
		match(apiErrorTag(error))
			.with("LocationNameTaken", () => t("error.nameTaken"))
			.with("LocationNotFound", () => t("error.notFound"))
			.with("DefaultLocation", () => t("remove.default"))
			.with("LocationHasStock", () => t("remove.hasStock"))
			.otherwise(() => tCommon("form.failed"));
}

function LocationsPage() {
	const { t } = useTranslation("location");
	const { t: tSettings } = useTranslation("settings");
	const queryClient = useQueryClient();
	const { data } = useSuspenseQuery(locationsQuery());
	const [editing, setEditing] = useState<string>();
	const errorMessage = useLocationErrorMessage();
	// Every stock screen shows the locations: refresh all of them.
	const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.stock.key() });
	const remove = useMutation({ ...orpc.stock.removeLocation.mutationOptions(), onSuccess: invalidate });

	const columns: readonly Column<Location>[] = [
		{
			id: "name",
			header: t("column.name"),
			cell: (location) =>
				editing === location.id ? (
					<RenameForm
						location={location}
						onDone={() => {
							setEditing(undefined);
							void invalidate();
						}}
					/>
				) : (
					<span className="flex flex-wrap items-center gap-2">
						<span className="font-medium">{location.name}</span>
						{location.isDefault ? <Badge variant="secondary">{t("default")}</Badge> : null}
					</span>
				),
		},
		{
			id: "quantity",
			header: t("column.quantity"),
			align: "right",
			cell: (location) => <span className="tabular-nums">{location.quantity}</span>,
		},
		{
			id: "createdAt",
			header: t("column.created"),
			secondary: true,
			cell: (location) => <time dateTime={location.createdAt}>{formatDate(location.createdAt)}</time>,
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("column.actions")}</span>,
			align: "right",
			cell: (location) =>
				editing === location.id ? null : (
					<span className="flex justify-end gap-1">
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label={t("rename.button", { name: location.name })}
							onClick={() => setEditing(location.id)}
						>
							<IconPencil aria-hidden />
						</Button>
						{location.isDefault ? null : (
							<ConfirmDialog
								trigger={
									<Button
										variant="ghost"
										size="icon-sm"
										aria-label={t("remove.button", { name: location.name })}
									>
										<IconArchive aria-hidden />
									</Button>
								}
								title={t("remove.title", { name: location.name })}
								description={t("remove.description")}
								confirmLabel={t("remove.confirm")}
								errorMessage={errorMessage}
								onConfirm={() => remove.mutateAsync({ locationId: location.id })}
							/>
						)}
					</span>
				),
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
			/>
			<DataTable
				label={t("title")}
				columns={columns}
				rows={data}
				rowKey={(location) => location.id}
				empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("empty")}</p>}
			/>
			<NewLocationCard onCreated={() => void invalidate()} />
		</div>
	);
}

function NewLocationCard({ onCreated }: { onCreated: () => void }) {
	const { t } = useTranslation("location");
	const errorMessage = useLocationErrorMessage();
	const form = useZodForm(LocationBody, { defaultValues: { name: "" } });
	const create = useMutation(orpc.stock.createLocation.mutationOptions());

	const onSubmit = async (values: LocationBody) => {
		try {
			await create.mutateAsync(values);
			form.reset();
			onCreated();
		} catch (error) {
			const field = apiErrorTag(error) === "LocationNameTaken" ? "name" : "root";
			form.setError(field, { type: "server", message: errorMessage(error) });
		}
	};

	return (
		<Card>
			<CardContent>
				<Form form={form} onSubmit={onSubmit}>
					<h2 className="font-heading font-medium">{t("new.title")}</h2>
					<TextField
						control={form.control}
						name="name"
						label={t("field.name")}
						description={t("field.nameDescription")}
						autoComplete="off"
					/>
					<FormError control={form.control} />
					<div>
						<SubmitButton control={form.control}>{t("new.submit")}</SubmitButton>
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}

/** The name, editable in its row: Enter saves, the cross leaves it as it was. */
function RenameForm({ location, onDone }: { location: Location; onDone: () => void }) {
	const { t } = useTranslation("location");
	const errorMessage = useLocationErrorMessage();
	const form = useZodForm(LocationBody, { defaultValues: { name: location.name } });
	const rename = useMutation(orpc.stock.renameLocation.mutationOptions());

	const onSubmit = async (values: LocationBody) => {
		try {
			await rename.mutateAsync({ locationId: location.id, ...values });
			onDone();
		} catch (error) {
			form.setError("name", { type: "server", message: errorMessage(error) });
		}
	};

	return (
		<Form form={form} onSubmit={onSubmit} className="gap-2">
			<div className="flex items-start gap-2">
				<div className="min-w-0 flex-1 [&_label]:sr-only">
					<TextField
						control={form.control}
						name="name"
						label={t("rename.label", { name: location.name })}
						autoComplete="off"
						autoFocus
						onKeyDown={(event) => {
							if (event.key === "Escape") onDone();
						}}
					/>
				</div>
				<SubmitButton control={form.control} size="icon" aria-label={t("rename.save")}>
					<IconCheck aria-hidden />
				</SubmitButton>
				<Button type="button" variant="ghost" size="icon" aria-label={t("rename.cancel")} onClick={onDone}>
					<IconX aria-hidden />
				</Button>
			</div>
		</Form>
	);
}
